import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Linking from 'expo-linking';
import { router } from 'expo-router';
import type { Session } from '@supabase/supabase-js';

import {
  canAccessRoleAccount,
  getRoleHomePath,
  sanitizeNextPath,
  type AppRole,
} from '@/lib/auth-routing';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';

type AuthFlow = 'idle' | 'confirmation' | 'recovery';
type Profile = {
  email: string;
  full_name: string | null;
  id: string;
  is_active: boolean;
  role: AppRole;
  school_id: string;
  school_name: string | null;
};

type AuthContextValue = {
  authFlow: AuthFlow;
  isConfigured: boolean;
  loading: boolean;
  profile: Profile | null;
  session: Session | null;
  setAuthFlow: (flow: AuthFlow) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function parseAuthPayload(url: string) {
  const [urlWithoutHash, hash = ''] = url.split('#');
  const query = urlWithoutHash.includes('?') ? urlWithoutHash.split('?')[1] : '';

  const queryParams = new URLSearchParams(query);
  const hashParams = new URLSearchParams(hash);

  return {
    accessToken: hashParams.get('access_token') ?? queryParams.get('access_token'),
    next: queryParams.get('next'),
    refreshToken: hashParams.get('refresh_token') ?? queryParams.get('refresh_token'),
    type: hashParams.get('type') ?? queryParams.get('type'),
  };
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authFlow, setAuthFlow] = useState<AuthFlow>('idle');
  const [profile, setProfile] = useState<Profile | null>(null);
  const lastProfileRequestKeyRef = useRef<string | null>(null);
  const inflightProfileRequestKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const clearLocalSession = async () => {
      setSession(null);
      setProfile(null);
      setAuthFlow('idle');
      lastProfileRequestKeyRef.current = null;
      inflightProfileRequestKeyRef.current = null;

      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // The refresh token may already be invalid or unavailable.
      }
    };

    const loadProfile = async (userId: string, schoolName?: string | null) => {
      const requestKey = `${userId}:${schoolName ?? ''}`;

      if (
        lastProfileRequestKeyRef.current === requestKey ||
        inflightProfileRequestKeyRef.current === requestKey
      ) {
        return profile;
      }

      inflightProfileRequestKeyRef.current = requestKey;

      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email, role, school_id, is_active')
        .eq('id', userId)
        .single();

      if (error) {
        console.warn('Failed to load profile.', error.message);
        setProfile(null);
        if (inflightProfileRequestKeyRef.current === requestKey) {
          inflightProfileRequestKeyRef.current = null;
        }
        return null;
      }

      if (!canAccessRoleAccount(data.role as AppRole, data.is_active)) {
        setProfile(null);
        if (inflightProfileRequestKeyRef.current === requestKey) {
          inflightProfileRequestKeyRef.current = null;
        }
        await clearLocalSession();
        return null;
      }

      const nextProfile = {
        ...(data as Omit<Profile, 'school_name'>),
        school_name: schoolName ?? null,
      };

      setProfile(nextProfile);
      lastProfileRequestKeyRef.current = requestKey;
      if (inflightProfileRequestKeyRef.current === requestKey) {
        inflightProfileRequestKeyRef.current = null;
      }

      return nextProfile;
    };

    const handleIncomingUrl = async (url: string) => {
      const { accessToken, next, refreshToken, type } = parseAuthPayload(url);

      if (!accessToken || !refreshToken) return;

      const { data, error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (error) {
        console.warn('Failed to restore Supabase session from URL.', error.message);
        return;
      }

      setSession(data.session);

      let resolvedProfile: Profile | null = null;

      if (data.session?.user.id) {
        resolvedProfile = await loadProfile(
          data.session.user.id,
          (data.session.user.user_metadata?.school_name as string | null | undefined) ?? null
        );
      }

      if (type === 'recovery') {
        setAuthFlow('recovery');
        router.replace('/forgot-password');
        return;
      }

      if (resolvedProfile) {
        const targetPath =
          sanitizeNextPath(next, resolvedProfile.role) ??
          getRoleHomePath(resolvedProfile.role);

        setAuthFlow('idle');
        router.replace(targetPath);
        return;
      }

      setAuthFlow('idle');
      router.replace('/sign-in');
    };

    const hydrate = async () => {
      const initialUrl = await Linking.getInitialURL();

      try {
        const { data } = await supabase.auth.getSession();

        if (!isMounted) return;

        setSession(data.session);
        if (data.session?.user.id) {
          await loadProfile(
            data.session.user.id,
            (data.session.user.user_metadata?.school_name as string | null | undefined) ?? null
          );
        }
      } catch (error) {
        console.warn('Failed to restore Supabase session.', error);
        if (isMounted) {
          await clearLocalSession();
        }
      }

      if (initialUrl) {
        await handleIncomingUrl(initialUrl);
      }

      if (!isMounted) return;
      setLoading(false);
    };

    void hydrate();

    const authSubscription = supabase.auth.onAuthStateChange((event, nextSession) => {
      setSession(nextSession);

      if (event === 'PASSWORD_RECOVERY') {
        setAuthFlow('recovery');
      }

      if (event === 'SIGNED_OUT') {
        setAuthFlow('idle');
        setProfile(null);
        lastProfileRequestKeyRef.current = null;
        inflightProfileRequestKeyRef.current = null;
      }

      if (nextSession?.user.id) {
        void loadProfile(
          nextSession.user.id,
          (nextSession.user.user_metadata?.school_name as string | null | undefined) ?? null
        );
      }
    });

    const urlSubscription = Linking.addEventListener('url', ({ url }) => {
      void handleIncomingUrl(url);
    });

    return () => {
      isMounted = false;
      authSubscription.data.subscription.unsubscribe();
      urlSubscription.remove();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      authFlow,
      isConfigured: isSupabaseConfigured,
      loading,
      profile,
      session,
      setAuthFlow,
    }),
    [authFlow, loading, profile, session]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }

  return context;
}
